'use client';

import { toast as sonnerToast, type ExternalToast } from 'sonner';
import toastEn from '@/locales/toast-en.json';
import toastEs from '@/locales/toast-es.json';
import type { Language } from '@/context/LanguageContext';

type ToastType = 'loading' | 'success' | 'error' | 'info' | 'message';

type TranslationMap = Record<string, string>;
type KnownMessageTranslations = Record<Language, string>;

const PREFS_KEY = 'betvault_prefs';

const TRANSLATIONS: Record<Exclude<Language, 'pt-BR'>, TranslationMap> = {
  en: toastEn as TranslationMap,
  es: toastEs as TranslationMap,
};

const KNOWN_MESSAGE_TRANSLATIONS: Record<string, KnownMessageTranslations> = {
  'New password should be different from the old password.': {
    'pt-BR': 'A nova senha deve ser diferente da senha anterior.',
    en: 'New password should be different from the old password.',
    es: 'La nueva contraseña debe ser diferente de la contraseña anterior.',
  },
  'Password should be at least 6 characters.': {
    'pt-BR': 'A senha deve ter pelo menos 6 caracteres.',
    en: 'Password should be at least 6 characters.',
    es: 'La contraseña debe tener al menos 6 caracteres.',
  },
  'Auth session missing!': {
    'pt-BR': 'Sua sessão de redefinição expirou. Solicite um novo link para continuar.',
    en: 'Your reset session expired. Request a new link to continue.',
    es: 'Tu sesión de restablecimiento expiró. Solicita un nuevo enlace para continuar.',
  },
  'Password update requires reauthentication': {
    'pt-BR': 'Sua sessão de redefinição expirou. Solicite um novo link para continuar.',
    en: 'Your reset session expired. Request a new link to continue.',
    es: 'Tu sesión de restablecimiento expiró. Solicita un nuevo enlace para continuar.',
  },
};

function getCurrentLanguage(): Language {
  if (typeof window === 'undefined') return 'pt-BR';
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return 'pt-BR';
    const parsed = JSON.parse(raw) as { language?: Language };
    return parsed.language ?? 'pt-BR';
  } catch {
    return 'pt-BR';
  }
}

function applyPatternTranslations(
  message: string,
  lang: Exclude<Language, 'pt-BR'>
): string | null {
  const text = message.trim();

  const patterns: Array<[RegExp, (groups: string[]) => string]> = [
    [
      /^Aposta liquidada:\s*(.+)$/i,
      ([result]) => (lang === 'en' ? `Bet settled: ${result}` : `Apuesta liquidada: ${result}`),
    ],
    [
      /^Aposta marcada como\s*(.+)$/i,
      ([result]) => (lang === 'en' ? `Bet marked as ${result}` : `Apuesta marcada como ${result}`),
    ],
    [
      /^Conta\s+(ativada|inativada|banida)\s+com\s+sucesso\.$/i,
      ([status]) => {
        if (lang === 'en') {
          const map: Record<string, string> = {
            ativada: 'activated',
            inativada: 'inactivated',
            banida: 'banned',
          };
          return `Account ${map[status.toLowerCase()] ?? status} successfully.`;
        }
        const map: Record<string, string> = {
          ativada: 'activada',
          inativada: 'inactivada',
          banida: 'baneada',
        };
        return `Cuenta ${map[status.toLowerCase()] ?? status} con éxito.`;
      },
    ],
    [
      /^Notificação enviada para\s*(\d+)\s+usuário\(s\)!$/i,
      ([count]) =>
        lang === 'en'
          ? `Notification sent to ${count} user(s)!`
          : `¡Notificación enviada a ${count} usuario(s)!`,
    ],
    [
      /^(.+)\s+agora é administrador\.$/i,
      ([email]) =>
        lang === 'en' ? `${email} is now an administrator.` : `${email} ahora es administrador.`,
    ],
    [
      /^(.+)\s+voltou a ser usuário comum\.$/i,
      ([email]) =>
        lang === 'en'
          ? `${email} is now a regular user again.`
          : `${email} volvió a ser un usuario común.`,
    ],
    [
      /^(.+)\s+atualizada com sucesso$/i,
      ([name]) =>
        lang === 'en' ? `${name} updated successfully` : `${name} actualizada con éxito`,
    ],
    [
      /^(.+)\s+adicionada com sucesso$/i,
      ([name]) => (lang === 'en' ? `${name} added successfully` : `${name} agregada con éxito`),
    ],
    [/^(.+)\s+arquivada$/i, ([name]) => (lang === 'en' ? `${name} archived` : `${name} archivada`)],
    [
      /^(.+)\s+reativada$/i,
      ([name]) => (lang === 'en' ? `${name} reactivated` : `${name} reactivada`),
    ],
    [
      /^Bem-vindo de volta,\s*(.+)!$/i,
      ([name]) => (lang === 'en' ? `Welcome back, ${name}!` : `¡Bienvenido de vuelta, ${name}!`),
    ],
  ];

  for (const [regex, formatter] of patterns) {
    const match = text.match(regex);
    if (match) return formatter(match.slice(1));
  }

  const prefixMap: Array<[string, string, string]> = [
    ['Erro ao atualizar senha: ', 'Error updating password: ', 'Error al actualizar contraseña: '],
    [
      'Erro ao processar solicitação: ',
      'Error processing request: ',
      'Error al procesar solicitud: ',
    ],
    ['Erro ao salvar ordem: ', 'Error saving order: ', 'Error al guardar orden: '],
  ];

  for (const [ptPrefix, enPrefix, esPrefix] of prefixMap) {
    if (message.startsWith(ptPrefix)) {
      const suffix = message.slice(ptPrefix.length);
      const translatedSuffix = KNOWN_MESSAGE_TRANSLATIONS[suffix]?.[lang] ?? suffix;
      return `${lang === 'en' ? enPrefix : esPrefix}${translatedSuffix}`;
    }
  }

  return null;
}

function translateText(text: string): string {
  const lang = getCurrentLanguage();
  const normalized = text.trim();
  const knownTranslation = KNOWN_MESSAGE_TRANSLATIONS[normalized]?.[lang];
  if (knownTranslation) return knownTranslation;
  if (lang === 'pt-BR') return text;

  const dict = TRANSLATIONS[lang];
  const direct = dict[text];
  if (direct) return direct;

  const fromPattern = applyPatternTranslations(text, lang);
  if (fromPattern) return fromPattern;

  return text;
}

function translateValue<T>(value: T): T {
  if (typeof value === 'string') {
    return translateText(value) as T;
  }
  return value;
}

function translateOptions<T extends ExternalToast | undefined>(opts: T): T {
  const next: ExternalToast = { closeButton: true, ...(opts ?? {}) };
  if (typeof next.description === 'string') {
    next.description = translateText(next.description);
  }
  return next as T;
}

function show(type: ToastType, message: unknown, opts?: ExternalToast) {
  const translatedMessage = translateValue(message);
  const translatedOpts = translateOptions(opts);

  switch (type) {
    case 'loading':
      return sonnerToast.loading(translatedMessage as any, translatedOpts);
    case 'success':
      return sonnerToast.success(translatedMessage as any, translatedOpts);
    case 'error':
      return sonnerToast.error(translatedMessage as any, translatedOpts);
    case 'info':
      return sonnerToast.info(translatedMessage as any, translatedOpts);
    default:
      return sonnerToast.message(translatedMessage as any, translatedOpts);
  }
}

const toastCallable = (message: unknown, opts?: ExternalToast) => show('message', message, opts);

export const toast = Object.assign(toastCallable, {
  loading: (message: unknown, opts?: ExternalToast) => show('loading', message, opts),
  success: (message: unknown, opts?: ExternalToast) => show('success', message, opts),
  error: (message: unknown, opts?: ExternalToast) => show('error', message, opts),
  info: (message: unknown, opts?: ExternalToast) => show('info', message, opts),
  message: (message: unknown, opts?: ExternalToast) => show('message', message, opts),
  dismiss: sonnerToast.dismiss,
  promise: sonnerToast.promise,
  custom: sonnerToast.custom,
});
